import React, { useEffect, useState, useContext, useRef } from 'react';
import { DatePicker, Radio } from 'antd';
import http from '../../../Http';
import utils from '../../../utils';
import moment from 'moment';
import ReactECharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';


const opt = {
    tooltip: {
        trigger: 'axis'
    },
    legend: {
        data: []
    },
    xAxis: {
        type: 'category',
        data: [],
        name: '(Time)',
        nameLocation: "center",
        nameGap: 25,
    },
    yAxis: {
        type: 'value',
        name: '(kWh)',
        nameLocation: "middle",
        nameRotate: 90,
        nameGap: 60,
    },
    series: []
}

function Energy({ SelectedCombinations }) {
    const { t } = useTranslation();
    const setTip = useContext(utils.tipContext)

    const [data, setData] = useState([]);
    const [timezone, setTimezone] = useState("UTC");
    const [date, setDate] = useState(moment().format('YYYY-MM-DD'));
    const [granularity, setGranularity] = useState('month');
    const [loading, setLoading] = useState(false);
    const [sns, setSns] = useState([]);

    const refChart = useRef(null);
    const refSNs = useRef([]);


    useEffect(() => {
        const handleResize = () => {
          if (refChart.current) {
            refChart.current.getEchartsInstance().resize();
          }
        };
    
        window.addEventListener('resize', handleResize);
    
        return () => {
          window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        if (!SelectedCombinations || SelectedCombinations.length === 0) {
            setSns([]);
            return;
        }
        let SNs = [];
        let tz = "";
        SelectedCombinations.forEach(v => {
            if (tz == "") {
                tz = SelectedCombinations[0].Timezone;
            }
            SNs.push(v.SN);
        })
        if (!tz) {
            tz = "UTC";
        }
        setTimezone(tz);
        if (JSON.stringify(SNs) === JSON.stringify(refSNs.current)) {
            let paralles = SelectedCombinations.length;
            if ( paralles> 0) {
                let tz = SelectedCombinations[0].Timezone??"UTC";
                opt.xAxis.name = t('(Time)') + " - " +tz;

                let ms = {};
                let ls = [];
                SelectedCombinations.forEach((v) => {
                    let dt = utils.convertTime(v["CreateTime"], v["LatestTimezone"], tz);
                    if (dt.substring(0, 10) != date.substring(0, 10)) {
                        return;
                    }
                    ms[v.SN] = {
                        SN: v.SN,
                        CreateTime: paralles==1 ? dt : dt.substring(0, 16)+":00",
                        EDayPV: v.EDayPV,
                        EDayToBattery: v.EDayToBattery,
                        EDayFromBattery: v.EDayFromBattery,
                        EDayLoad: v.EDayLoad,
                        EDayEPS: v.EDayEPS,
                        EDayToGrid: v.EDayToGrid,
                        EDayFromGrid: v.EDayFromGrid,

                        EMonthPV: v.EMonthPV,
                        EMonthToBattery: v.EMonthToBattery,
                        EMonthFromBattery: v.EMonthFromBattery,
                        EMonthLoad: v.EMonthLoad,
                        EMonthEPS: v.EMonthEPS,
                        EMonthToGrid: v.EMonthToGrid,
                        EMonthFromGrid: v.EMonthFromGrid,

                        EYearPV: v.EYearPV,
                        EYearToBattery: v.EYearToBattery,
                        EYearFromBattery: v.EYearFromBattery,
                        EYearLoad: v.EYearLoad,
                        EYearEPS: v.EYearEPS,
                        EYearToGrid: v.EYearToGrid,
                        EYearFromGrid: v.EYearFromGrid,

                        ETotalPV: v.ETotalPV,
                        ETotalToBattery: v.ETotalToBattery,
                        ETotalFromBattery: v.ETotalFromBattery,
                        ETotalLoad: v.ETotalLoad,
                        ETotalEPS: v.ETotalEPS,
                        ETotalToGrid: v.ETotalToGrid,
                        ETotalFromGrid: v.ETotalFromGrid,

                    };
                    ls.push(ms[v.SN])
                });

                let replaced = false;
                data.forEach(v=>{
                    if (v.SN) {
                        let r = ms[v.SN];
                        if (r && r["CreateTime"] == v["CreateTime"]) {
                            Object.assign(v, r);
                            replaced = true;
                            let idx = ls.indexOf(r);
                            if (idx>-1) {
                                ls.splice(idx, 1);
                            }
                            delete(ms[v.SN]);
                        }
                    }
                });
                if (!replaced && ls.length > 0) {
                    setData([...data, ...ls]);
                }
            }

            return;
        }
        setSns(SNs);
        refSNs.current = SNs;
    }, [SelectedCombinations])

    useEffect(() => {
        if (!sns || sns.length === 0) {
            return;
        }
        if (loading) {
            return;
        }
        setLoading(true);

        let SNs = [];
        SelectedCombinations.forEach(v => {
            SNs.push(v.SN);
        })
        let paralles = SNs?.length??1;

        let active = true;
        if (granularity == 'month') { 
            //取月数据
            (async () => {
                let resp = await http.post("integrated-inverter/uhome-threephase/report/monthly/report", {
                    SerialNumber: SNs.length === 1 ? SNs[0] : "",
                    SerialNumbers: SNs.join(","),
                    Date: moment(date).format('YYYY-MM') + '-01'
                }).then(async (r) => {
                    return await r.json();
                }).catch((e) => {
                    return null;
                });

                if (!active) {
                    return;
                }
                //console.log('Month Energy data:', resp.data)

                if (resp === null || resp.code !== 200) {  //请求月数据失败
                    setTip({ open: true, severity: "error", msg: (resp && resp.msg) ? resp.msg : t("load inverter record error") });
                    return;
                } else {  //请求月数据成功
                    setData(resp.data);
                }

                //渲染数据到页面
                const instance = refChart.current.getEchartsInstance();
                if (instance) {
                    instance.setOption(opt, true);
                }
            })();
        } else if (granularity == 'year') { //年数据
            //请求数据
            (async () => {
                let resp = await http.post("integrated-inverter/uhome-threephase/report/yearly/report", {
                    SerialNumber: SNs.length === 1 ? SNs[0] : "",
                    SerialNumbers: SNs.join(","),
                    Date: moment(date).format('YYYY') + '-01-01'
                }).then(async (r) => {
                    return await r.json();
                }).catch((e) => {
                    return null;
                });

                if (!active) {
                    return;
                }

                if (resp === null || resp.code !== 200) { //请求年数据失败
                    setTip({ open: true, severity: "error", msg: (resp && resp.msg) ? resp.msg : t("load inverter record error") });
                    return;
                } else {  //请求年数据成功
                    setData(resp.data);
                }

                //渲染数据到页面
                const instance = refChart.current.getEchartsInstance();
                if (instance) {
                    instance.setOption(opt, true);
                }
            })();
        } else { //总体数据
            //取总体数据
            (async () => {
                let resp = await http.post("integrated-inverter/uhome-threephase/report/all-years/report", {
                    SerialNumber: SNs.length === 1 ? SNs[0] : "",
                    SerialNumbers: SNs.join(","),
                }).then(async (r) => {
                    return await r.json();
                }).catch((e) => {
                    return null;
                });

                if (!active) {
                    return;
                }

                if (resp === null || resp.code !== 200) {  //请求总体数据失败
                    setTip({ open: true, severity: "error", msg: (resp && resp.msg) ? resp.msg : t("load inverter record error") });
                    return;
                } else {  //请求总体数据成功
                    setData(resp.data);
                }

                //渲染数据到页面
                const instance = refChart.current.getEchartsInstance();
                if (instance) {
                    instance.setOption(opt, true);
                }                
            })();
        }
        setLoading(false);

        return () => {
            active = false;
        };
    }, [sns, date, granularity])

    useEffect(()=>{
        let paralles = sns?.length??1;
        if (granularity == 'month') { 
            //坐标, 计算每个月有多少天
            const y = moment(date).year()
            const m = moment(date).month()
            let days = Array(new Date(y, m + 1, 0).getDate())
            days.fill(0).forEach((v, i) => {
                days[i] = i + 1;
            })
            //坐标
            opt.xAxis.data = days;
            opt.xAxis.name = t('(Day)') + (timezone ? " - "+timezone : "");
            opt.yAxis.name = t('(kWh)');

            let EDayPV = Array(days.length).fill(null) 
            let EDayToGrid = Array(days.length).fill(null)
            let EDayFromGrid = Array(days.length).fill(null)
            let EDayLoad = Array(days.length).fill(null)
            let EDayEPS = Array(days.length).fill(null)
            let EDayToBattery = Array(days.length).fill(null)
            let EDayFromBattery = Array(days.length).fill(null)
            if (paralles == 1) {
                data.forEach(v => {
                    let dt = moment(v['CreateTime']).date() - 1;
                    EDayPV[dt] = (v['EDayPV']*0.01).toFixed(2)
                    EDayToGrid[dt] = (v['EDayToGrid']*0.01).toFixed(2)
                    EDayFromGrid[dt] = (v['EDayFromGrid']*0.01).toFixed(2)
                    EDayLoad[dt] = (v['EDayLoad']*0.01).toFixed(2)
                    EDayEPS[dt] = (v['EDayEPS']*0.01).toFixed(2)
                    EDayToBattery[dt] = (v['EDayToBattery']*0.01).toFixed(2)
                    EDayFromBattery[dt] = (v['EDayFromBattery']*0.01).toFixed(2)
                })
            } else {
                data.forEach(v => {
                    let dt = moment(v['CreateTime']).date() - 1;
                    if (EDayPV[dt] == null) { 
                        EDayPV[dt] = v['EDayPV']
                    } else {
                        EDayPV[dt] += v['EDayPV']
                    }
                    if (EDayToGrid[dt] == null) { 
                        EDayToGrid[dt] = v['EDayToGrid']
                    } else {
                        EDayToGrid[dt] += v['EDayToGrid']
                    }
                    if (EDayFromGrid[dt] == null) { 
                        EDayFromGrid[dt] = v['EDayFromGrid']
                    } else {
                        EDayFromGrid[dt] += v['EDayFromGrid']
                    }
                    if (EDayLoad[dt] == null) { 
                        EDayLoad[dt] = v['EDayLoad']
                    } else {
                        EDayLoad[dt] += v['EDayLoad']
                    }
                    if (EDayEPS[dt] == null) { 
                        EDayEPS[dt] = v['EDayEPS']
                    } else {
                        EDayEPS[dt] += v['EDayEPS']
                    }
                    if (EDayToBattery[dt] == null) { 
                        EDayToBattery[dt] = v['EDayToBattery']
                    } else {
                        EDayToBattery[dt] += v['EDayToBattery']
                    }
                    if (EDayFromBattery[dt] == null) { 
                        EDayFromBattery[dt] = v['EDayFromBattery']
                    } else {
                        EDayFromBattery[dt] += v['EDayFromBattery']
                    }
                })
                EDayPV.forEach((v, i) => {
                    EDayPV[i]!=null && (EDayPV[i] = (v*0.01).toFixed(2))
                })
                EDayToGrid.forEach((v, i) => {
                    EDayToGrid[i]!=null && (EDayToGrid[i] = (v*0.01).toFixed(2))
                })
                EDayFromGrid.forEach((v, i) => {
                    EDayFromGrid[i]!=null && (EDayFromGrid[i] = (v*0.01).toFixed(2))
                })
                EDayLoad.forEach((v, i) => {
                    EDayLoad[i]!=null && (EDayLoad[i] = (v*0.01).toFixed(2))
                })
                EDayEPS.forEach((v, i) => {
                    EDayEPS[i]!=null && (EDayEPS[i] = (v*0.01).toFixed(2))
                })
                EDayToBattery.forEach((v, i) => {
                    EDayToBattery[i]!=null && (EDayToBattery[i] = (v*0.01).toFixed(2))
                })
                EDayFromBattery.forEach((v, i) => {
                    EDayFromBattery[i]!=null && (EDayFromBattery[i] = (v*0.01).toFixed(2))
                })
            }
            //Echart坐标legend
            opt.legend.data = [t('Solar Production'), t('Battery Charge'), t('Battery Discharge'), t('Export to Grid'), t('Import from Grid'), t('Consumption')]
            //Echart数据
            opt.series = [{
                name: t('Solar Production'),
                type: 'bar',
                data: EDayPV
            }, {
                name: t('Battery Charge'),
                type: 'bar',
                data: EDayToBattery
            }, {
                name: t('Battery Discharge'),
                type: 'bar',
                data: EDayFromBattery
            }, {
                name: t('Export to Grid'),
                type: 'bar',
                data: EDayToGrid
            }, {
                name: t('Import from Grid'),
                type: 'bar',
                data: EDayFromGrid
            }, {
                name: t('Load'),
                type: 'bar',
                data: EDayLoad
            }, {
                name: t('EPS'),
                type: 'bar',
                data: EDayEPS
            }]

            //渲染数据到页面
            const instance = refChart.current.getEchartsInstance();
            if (instance) {
                instance.setOption(opt, true);
            }
            
        } else if (granularity == 'year') { //年数据
            //坐标
            opt.xAxis.data = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
            opt.xAxis.name = t('(Month)') + (timezone ? " - "+timezone : "");
            opt.yAxis.name = t('(kWh)');
            //请求数据
            let EMonthPV = Array(12).fill(null)
            let EMonthToGrid = Array(12).fill(null)
            let EMonthFromGrid = Array(12).fill(null)
            let EMonthLoad = Array(12).fill(null)
            let EMonthEPS = Array(12).fill(null)
            let EMonthToBattery = Array(12).fill(null)
            let EMonthFromBattery = Array(12).fill(null)
            if (paralles == 1) {
                data.forEach(v => {
                    let dt = moment(v['YM']).month();   
                    EMonthPV[dt] = (v['EMonthPV']*0.01).toFixed(2)
                    EMonthToGrid[dt] = (v['EMonthToGrid']*0.01).toFixed(2)
                    EMonthFromGrid[dt] = (v['EMonthFromGrid']*0.01).toFixed(2)
                    EMonthLoad[dt] = (v['EMonthLoad']*0.01).toFixed(2)
                    EMonthEPS[dt] = (v['EMonthEPS']*0.01).toFixed(2)
                    EMonthToBattery[dt] = (v['EMonthToBattery']*0.01).toFixed(2)
                    EMonthFromBattery[dt] = (v['EMonthFromBattery']*0.01).toFixed(2)
                })
            } else {
                data.forEach(v => {
                    let dt = moment(v['YM']).month();
                    if (EMonthPV[dt] == null) { 
                        EMonthPV[dt] = v['EMonthPV']
                    } else {
                        EMonthPV[dt] += v['EMonthPV']
                    }
                    if (EMonthToGrid[dt] == null) { 
                        EMonthToGrid[dt] = v['EMonthToGrid']
                    } else {
                        EMonthToGrid[dt] += v['EMonthToGrid']
                    }
                    if (EMonthFromGrid[dt] == null) { 
                        EMonthFromGrid[dt] = v['EMonthFromGrid']
                    } else {
                        EMonthFromGrid[dt] += v['EMonthFromGrid']
                    }
                    if (EMonthLoad[dt] == null) { 
                        EMonthLoad[dt] = v['EMonthLoad']
                    } else {
                        EMonthLoad[dt] += v['EMonthLoad']
                    }
                    if (EMonthEPS[dt] == null) { 
                        EMonthEPS[dt] = v['EMonthEPS']
                    } else {
                        EMonthEPS[dt] += v['EMonthEPS']
                    }
                    if (EMonthToBattery[dt] == null) { 
                        EMonthToBattery[dt] = v['EMonthToBattery']
                    } else {
                        EMonthToBattery[dt] += v['EMonthToBattery']
                    }
                    if (EMonthFromBattery[dt] == null) { 
                        EMonthFromBattery[dt] = v['EMonthFromBattery']
                    } else {
                        EMonthFromBattery[dt] += v['EMonthFromBattery']
                    }
                })
                EMonthPV.forEach((v, i) => {
                    EMonthPV[i]!=null && (EMonthPV[i] = (v*0.01).toFixed(2))
                })
                EMonthToGrid.forEach((v, i) => {
                    EMonthToGrid[i]!=null && (EMonthToGrid[i] = (v*0.01).toFixed(2))
                })
                EMonthFromGrid.forEach((v, i) => {
                    EMonthFromGrid[i]!=null && (EMonthFromGrid[i] = (v*0.01).toFixed(2))
                })
                EMonthLoad.forEach((v, i) => {
                    EMonthLoad[i]!=null && (EMonthLoad[i] = (v*0.01).toFixed(2))
                })
                EMonthEPS.forEach((v, i) => {
                    EMonthEPS[i]!=null && (EMonthEPS[i] = (v*0.01).toFixed(2))
                })
                EMonthToBattery.forEach((v, i) => {
                    EMonthToBattery[i]!=null && (EMonthToBattery[i] = (v*0.01).toFixed(2))
                })
                EMonthFromBattery.forEach((v, i) => {
                    EMonthFromBattery[i]!=null && (EMonthFromBattery[i] = (v*0.01).toFixed(2))
                })
            }
            //Echart坐标legend
            opt.legend.data = [t('Solar Production'), t('Battery Charge'), t('Battery Discharge'), t('Export to Grid'), t('Import from Grid'), t('Consumption')]
            //Echart数据
            opt.series = [{
                name: t('Solar Production'),
                type: 'bar',
                data: EMonthPV
            }, {
                name: t('Battery Charge'),
                type: 'bar',
                data: EMonthToBattery
            }, {
                name: t('Battery Discharge'),
                type: 'bar',
                data: EMonthFromBattery
            }, {
                name: t('Export to Grid'),
                type: 'bar',
                data: EMonthToGrid
            }, {
                name: t('Import from Grid'),
                type: 'bar',
                data: EMonthFromGrid
            }, {
                name: t('Load'),
                type: 'bar',
                data: EMonthLoad
            }, {
                name: t('EPS'),
                type: 'bar',
                data: EMonthEPS
            }]

            //渲染数据到页面
            const instance = refChart.current.getEchartsInstance();
            if (instance) {
                instance.setOption(opt, true);
            }
        } else { //总体数据
            //显示近10年的数据
            const curYear = new Date().getFullYear()
            let days = Array(10).fill(0)
            days.fill(0).forEach((v, i) => {
                days[i] = curYear - 9 + i
            })
            //坐标
            opt.xAxis.data = days;
            opt.xAxis.name = t('(Year)') + (timezone ? " - "+timezone : "");
            opt.yAxis.name = t('(kWh)');
            //取总体数据
            
            let EYearPV = Array(10)
            let EYearToBattery = Array(10)
            let EYearFromBattery = Array(10)
            let EYearToGrid = Array(10)
            let EYearFromGrid = Array(10)
            let EYearLoad = Array(10)
            let EYearEPS = Array(10)
            if (paralles == 1) {
                data.forEach(v => {
                    let dt = moment(v['Y']).year() - curYear + 9;
                    EYearPV[dt] = ((v['EYearPV']??0)*0.01).toFixed(2)
                    EYearToGrid[dt] = ((v['EYearToGrid']??0)*0.01).toFixed(2)
                    EYearFromGrid[dt] = ((v['EYearFromGrid']??0)*0.01).toFixed(2)
                    EYearLoad[dt] = ((v['EYearLoad']??0)*0.01).toFixed(2)
                    EYearEPS[dt] = ((v['EYearEPS']??0)*0.01).toFixed(2)
                    EYearToBattery[dt] = ((v['EYearToBattery']??0)*0.01).toFixed(2)
                    EYearFromBattery[dt] = ((v['EYearFromBattery']??0)*0.01).toFixed(2)
                })
            } else {
                data.forEach(v => {
                    let dt = moment(v['Y']).year() - curYear + 9;
                    if (EYearPV[dt] == null) { 
                        EYearPV[dt] = v['EYearPV']
                    } else {
                        EYearPV[dt] += v['EYearPV']
                    }
                    if (EYearToGrid[dt] == null) { 
                        EYearToGrid[dt] = v['EYearToGrid']
                    } else {
                        EYearToGrid[dt] += v['EYearToGrid']
                    }
                    if (EYearFromGrid[dt] == null) { 
                        EYearFromGrid[dt] = v['EYearFromGrid']
                    } else {
                        EYearFromGrid[dt] += v['EYearFromGrid']
                    }
                    if (EYearLoad[dt] == null) { 
                        EYearLoad[dt] = v['EYearLoad']
                    } else {
                        EYearLoad[dt] += v['EYearLoad']
                    }
                    if (EYearEPS[dt] == null) { 
                        EYearEPS[dt] = v['EYearEPS']
                    } else {
                        EYearEPS[dt] += v['EYearEPS']
                    }
                    if (EYearToBattery[dt] == null) { 
                        EYearToBattery[dt] = v['EYearToBattery']
                    } else {
                        EYearToBattery[dt] += v['EYearToBattery']
                    }
                    if (EYearFromBattery[dt] == null) { 
                        EYearFromBattery[dt] = v['EYearFromBattery']
                    } else {
                        EYearFromBattery[dt] += v['EYearFromBattery']
                    }
                })
                EYearPV.forEach((v, i) => {
                    EYearPV[i] != null && (EYearPV[i] = (v*0.01).toFixed(2))
                })
                EYearToGrid.forEach((v, i) => {
                    EYearToGrid[i] != null && (EYearToGrid[i] = (v*0.01).toFixed())
                })
                EYearFromGrid.forEach((v, i) => {
                    EYearFromGrid[i] != null && (EYearFromGrid[i] = (v*0.01).toFixed(2))
                })
                EYearLoad.forEach((v, i) => {
                    EYearLoad[i] != null && (EYearLoad[i] = (v*0.01).toFixed(2))
                })
                EYearEPS.forEach((v, i) => {
                    EYearEPS[i] != null && (EYearEPS[i] = (v*0.01).toFixed(2))
                })
                EYearToBattery.forEach((v, i) => {
                    EYearToBattery[i] != null && (EYearToBattery[i] = (v*0.01).toFixed(2))
                })
                EYearFromBattery.forEach((v, i) => {
                    EYearFromBattery[i] != null && (EYearFromBattery[i] = (v*0.01).toFixed(2))
                })
            }
            //Echart坐标legend
            opt.legend.data = [t('Solar Production'), t('Battery Charge'), t('Battery Discharge'), t('Export to Grid'), t('Import from Grid'), t('Consumption')]
            //Echart数据
            opt.series = [{
                name: t('Solar Production'),
                type: 'bar',
                data: EYearPV
            }, {
                name: t('Battery Charge'),
                type: 'bar',
                data: EYearToBattery
            }, {
                name: t('Battery Discharge'),
                type: 'bar',
                data: EYearFromBattery
            }, {
                name: t('Export to Grid'),
                type: 'bar',
                data: EYearToGrid
            }, {
                name: t('Import from Grid'),
                type: 'bar',
                data: EYearFromGrid
            }, {
                name: t('Load'),
                type: 'bar',
                data: EYearLoad
            }, {
                name: t('EPS'),
                type: 'bar',
                data: EYearEPS
            }]

            //渲染数据到页面
            const instance = refChart.current.getEchartsInstance();
            if (instance) {
                instance.setOption(opt, true);
            }
        }
    }, [data])

    return (<div style={{width: '100%', height: '100%',
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        padding:"10px", 
    }}>
        <div style={{
            flex:"0 0 auto",
            display:"flex", 
            flexDirection:"row",
            justifyContent:"space-between",
            alignItems:"center",
        }}>
            <div style={{
                flex:"0 0 auto",
                fontSize:"18px", fontWeight:"500", marginBottom:"10px"
            }}>
                {t('Energy Overview')} {granularity == 'month' ? '(' + moment(date).format('YYYY-MM') + ')' : granularity == 'year' ? '(' + moment(date).format('YYYY') + ')' : null}
            </div>
            <div style={{
                flex:"0 0 auto",
                display:"flex", 
                flexDirection:"row",
                justifyContent:"flex-end",
            }}>
                <div style={{flex:"0 0 auto"}}>
                    <Radio.Group value={granularity} optionType='button' buttonStyle='solid' onChange={e => {
                        if (e.target.value != granularity) {
                            setData([]);
                        }
                        setGranularity(e.target.value)
                    }}>
                        <Radio.Button value="month">{t('Month')}</Radio.Button>
                        <Radio.Button value="year">{t('Year')}</Radio.Button>
                        <Radio.Button value="total">{t('Total')}</Radio.Button>
                    </Radio.Group>
                </div>
                <div style={{ flex: "0 0 auto", display: granularity == 'total' ? 'none' : 'block' }}>
                    <DatePicker
                        picker={granularity == "month" ? 'month' : 'year'}
                        onChange={(_, ds) => setDate(ds)}
                        defaultValue={moment()}
                        value={moment(date)}
                        allowClear={false}
                    />
                </div>
            </div>
        </div>
        <div style={{ flex:"auto", position:"relative" }}>
            <ReactECharts ref={refChart} option={opt} style={{ width: "100%", height: "100%" }} />
        </div>
    </div>)
}

export default Energy;